import { MappedReservedTimes } from 'components/Form/BDatePicker';
import { useMemo } from 'react';
import { dateService } from 'services/DateService';
import { VehicleBasedDemandState } from 'types/demand.type';
import { HourlyRentalInfo, ReservedTime } from 'types/vehicle.type';
import { usePriceWidgetHourGenerator } from './usePriceWidgetHourGenerator';
import { usePriceWidgetIsSpecialDate } from './usePriceWidgetIsSpecialDate';

export function usePriceWidgetReservedTimes(
  state: VehicleBasedDemandState,
  reservedTimes: ReservedTime[],
  rentalInfo: HourlyRentalInfo
) {
  const dayjs = dateService.getDayjs();
  const { generateHourArray } = usePriceWidgetHourGenerator();
  const { getIsSpecialDate } = usePriceWidgetIsSpecialDate(rentalInfo);

  const createTourDisabledHours = (
    time: ReservedTime,
    dateTimeMapper: MappedReservedTimes
  ) => {
    const tourStartTime = dayjs(time.dateTimeRange.startTime);
    const tourEndTime = dayjs(time.dateTimeRange.endTime);
    const tourDuration = tourEndTime.diff(tourStartTime, 'hour');

    const addDisabledTourHours = () => {
      [...Array(tourDuration)].forEach((_, idx) => {
        const date = tourStartTime.add(idx, 'hour');
        const formattedDate = dateService.getFormattedDate(date.toDate());
        if (!dateTimeMapper[formattedDate]) {
          dateTimeMapper[formattedDate] = [];
        }
        const harbor = idx === 0 ? time.startHarbor.id : time.endHarbor.id;
        dateTimeMapper[formattedDate].push({
          hour: date.hour(),
          harborId: harbor,
          isOffset: false,
        });
      });
    };

    return {
      harborId: time.startHarbor.id,
      tourStartTime,
      tourEndTime,
      tourDuration,
      addDisabledTourHours,
    };
  };

  const mappedReservedTimes = useMemo(() => {
    const dateTimeMapper: MappedReservedTimes = {};
    if (state.isSwimmingTour) {
      reservedTimes.forEach((time) => {
        const swimmingTimes = { start: 10, end: 18 };
        const { tourStartTime, tourEndTime, addDisabledTourHours } =
          createTourDisabledHours(time, dateTimeMapper);
        const startTimeHour = tourStartTime.hour();
        const endTimeHour = tourEndTime.hour();
        if (
          startTimeHour > swimmingTimes.start &&
          startTimeHour < swimmingTimes.end
        ) {
          const onlyDate = dateService.getFormattedDate(tourStartTime.toDate());
          dateTimeMapper[onlyDate] = generateHourArray().map((hour) => ({
            hour,
          }));
        } else if (
          endTimeHour > swimmingTimes.start &&
          endTimeHour < swimmingTimes.end
        ) {
          const onlyDate = dateService.getFormattedDate(tourEndTime.toDate());
          dateTimeMapper[onlyDate] = generateHourArray().map((hour) => ({
            hour,
          }));
        } else {
          addDisabledTourHours();
        }
      });
    } else {
      reservedTimes.forEach((time) => {
        const { harborId, tourStartTime, addDisabledTourHours } =
          createTourDisabledHours(time, dateTimeMapper);
        const isSpecialDate = getIsSpecialDate(tourStartTime.toDate());
        const startHourOffset = isSpecialDate
          ? rentalInfo.minimumRentDurationOnSpecialDays
          : rentalInfo.minimumRentDuration;

        const prevOffsetCalculate = startHourOffset - tourStartTime.hour() - 1;
        const isOffsetMoreThanOneDay = prevOffsetCalculate > 0;
        const regularPreviousDateOffset = isOffsetMoreThanOneDay
          ? prevOffsetCalculate
          : 0;

        const previousDate = tourStartTime.subtract(1, 'day');

        const isPreviousDateSpecialDate = getIsSpecialDate(
          previousDate.toDate()
        );
        const previosDateHourOffset = isPreviousDateSpecialDate
          ? rentalInfo.minimumRentDurationOnSpecialDays
          : rentalInfo.minimumRentDuration;
        const previousDateHourOffsetFromStartDate =
          previosDateHourOffset - tourStartTime.hour();
        const isPreviousDateHourOffsetRequiresDateTransition =
          previosDateHourOffset > startHourOffset &&
          previousDateHourOffsetFromStartDate > 0;
        const previousDayOffset = isPreviousDateHourOffsetRequiresDateTransition
          ? previousDateHourOffsetFromStartDate
          : regularPreviousDateOffset;

        const tourDateOffset = startHourOffset - regularPreviousDateOffset - 1;
        const tourDateOffestLen = tourDateOffset >= 0 ? tourDateOffset : 0;
        [...Array(tourDateOffestLen)].forEach((_, idx) => {
          const date = tourStartTime.subtract(+idx + 1, 'hour');
          const formattedDate = dateService.getFormattedDate(date.toDate());
          if (!dateTimeMapper[formattedDate]) {
            dateTimeMapper[formattedDate] = [];
          }
          const isHourExists = dateTimeMapper[formattedDate].find(
            (a) => a.hour === date.hour()
          );
          if (!isHourExists) {
            dateTimeMapper[formattedDate].push({
              hour: date.hour(),
              harborId,
              isOffset: true,
            });
          }
        });

        addDisabledTourHours();

        const previousDateLastHour = 23;
        [...Array(previousDayOffset)].forEach((_, idx) => {
          const date = previousDate;
          const formattedDate = dateService.getFormattedDate(date.toDate());
          const dateHour = previousDateLastHour - idx;
          if (!dateTimeMapper[formattedDate]) {
            dateTimeMapper[formattedDate] = [];
          }
          const isHourExists = dateTimeMapper[formattedDate].find(
            (a) => a.hour === dateHour
          );
          if (!isHourExists) {
            dateTimeMapper[formattedDate].push({
              hour: dateHour,
              harborId,
              isOffset: true,
            });
          }
        });
      });
    }
    Object.entries(dateTimeMapper).forEach(([key, val]) => {
      const hours = [...new Set(val)].sort((a, b) => a.hour - b.hour);
      hours.forEach((item) => {
        const idx = hours.findIndex(
          (v) =>
            v.hour === item.hour &&
            v.isOffset !== item.isOffset &&
            item.isOffset
        );
        idx !== -1 && hours.splice(idx, 1);
      });
      dateTimeMapper[key] = hours;
    });

    return dateTimeMapper;
  }, [
    reservedTimes,
    rentalInfo.minimumRentDuration,
    rentalInfo.minimumRentDurationOnSpecialDays,
    state.isSwimmingTour,
  ]);

  return {
    mappedReservedTimes,
  };
}
